import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import ServiceIllustration from '../components/ServiceIllustration';
import './Services.css';

const Services = () => {
  useEffect(() => {
    document.title = 'Services - SOFTORA Technologies';
  }, []);

  const serviceList = [
    {
      id: "web-dev",
      number: "01",
      title: "Web Application Development",
      category: "FRONTEND & FULL-STACK",
      description: "Modern, responsive, and blazingly fast web applications built with React, Next.js, and cutting-edge web technologies engineered for peak performance and conversion.",
      features: [
        "Interactive Single-Page Applications (SPA)",
        "Server-Side Rendering (SSR) & Static Site Generation (SSG)",
        "Modern Responsive UI with micro-animations",
        "SEO Optimization & Core Web Vitals perfection"
      ],
      tags: ["React.js", "Next.js", "JavaScript/TypeScript", "CSS3/Tailwind", "REST APIs"]
    },
    {
      id: "mobile-dev",
      number: "02",
      title: "Mobile App Development",
      category: "CROSS-PLATFORM (iOS & ANDROID)",
      description: "Cross-platform mobile applications using Flutter and Dart, delivering native performance, smooth animations, and uniform UI across both iOS and Android from a single codebase.",
      features: [
        "Native performance with Flutter 60fps rendering",
        "Hardware integration (Camera, GPS, Bluetooth, Push Notifications)",
        "Offline-first architecture with local cache synchronization",
        "App Store & Google Play Store release management"
      ],
      tags: ["Flutter", "Dart", "iOS", "Android", "State Management"]
    },
    {
      id: "backend-cloud",
      number: "03",
      title: "Backend & Cloud Infrastructure",
      category: "SCALABLE SYSTEMS & CLOUD",
      description: "Robust, enterprise-grade backend microservices and serverless architectures using Firebase, Node.js, and cloud platforms for uncompromising stability and security.",
      features: [
        "Real-time database management with Firebase Firestore",
        "Serverless Cloud Functions and automated webhooks",
        "Role-based access control (RBAC) & OAuth security",
        "High-throughput RESTful and GraphQL API services"
      ],
      tags: ["Firebase", "Node.js", "Express.js", "MySQL", "PostgreSQL", "MongoDB", "AWS"]
    },
    {
      id: "ai-solutions",
      number: "04",
      title: "AI Integration & Automation",
      category: "INTELLIGENT COMPUTING",
      description: "Harness the power of Artificial Intelligence to automate tedious business workflows, integrate AI agents, and build intelligent recommendation systems.",
      features: [
        "LLM & Generative AI model integrations (OpenAI, Claude, Gemini)",
        "Intelligent document parsing and data extraction",
        "WhatsApp & Web customer service chatbots",
        "Custom automation pipelines and predictive models"
      ],
      tags: ["Python", "OpenAI APIs", "LangChain", "NLP", "Process Automation"]
    },
    {
      id: "consulting",
      number: "05",
      title: "IT Architecture & Tech Consulting",
      category: "STRATEGIC ADVISORY",
      description: "Expert technical advisory and architectural blueprints to help startups and established businesses select the ideal tech stack, minimize technical debt, and scale safely.",
      features: [
        "Technology stack feasibility & selection",
        "Software architecture design & code audit",
        "Legacy codebase refactoring & modernization",
        "Scalability and cost optimization consulting"
      ],
      tags: ["System Architecture", "Code Review", "DevOps Strategy", "Database Design"]
    },
    {
      id: "qa-testing",
      number: "06",
      title: "Quality Assurance & Testing",
      category: "SECURITY & RELIABILITY",
      description: "Comprehensive QA engineering to guarantee that your applications are bug-free, highly resilient under heavy user loads, and compliant with modern security standards.",
      features: [
        "Automated unit & end-to-end testing",
        "Cross-browser and multi-device compatibility testing",
        "API stress testing and performance benchmarking",
        "Penetration testing and vulnerability mitigation"
      ],
      tags: ["Jest", "Cypress", "Postman", "Load Testing", "Security Audits"]
    },
    {
      id: "uiux-design",
      number: "07",
      title: "UI/UX Product Design",
      category: "EXPERIENCE DESIGN",
      description: "Human-centric digital interfaces designed to wow users, maintain brand elegance, and ensure intuitive usability across mobile and desktop devices.",
      features: [
        "Wireframing & interactive Figma prototypes",
        "Design systems, typography & color guidelines",
        "User flow & journey mapping",
        "Dark/Light mode themes & micro-interactions"
      ],
      tags: ["Figma", "UI/UX", "Design Systems", "Prototyping"]
    },
    {
      id: "documentation",
      number: "08",
      title: "Technical Documentation & SRS",
      category: "SPECIFICATIONS & MANUALS",
      description: "Detailed, publication-ready technical documentation, Software Requirement Specifications (SRS), database diagrams, and API guides for academic and enterprise audits.",
      features: [
        "Comprehensive Software Requirement Specifications (SRS)",
        "API Reference documentation & Swagger setup",
        "UML Diagrams, ER diagrams, and flowcharts",
        "Deployment guidelines & system handover manuals"
      ],
      tags: ["SRS", "UML", "API Docs", "Technical Writing"]
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Discovery & Scope",
      desc: "We analyze your project goals, user personas, and technical specifications to build a clear roadmap."
    },
    {
      step: "02",
      title: "UI/UX & Architecture",
      desc: "We design wireframes, design systems, and data models before writing a single line of production code."
    },
    {
      step: "03",
      title: "Agile Development",
      desc: "We develop in iterative bi-weekly sprints with continuous integration, review sessions, and transparent updates."
    },
    {
      step: "04",
      title: "QA & Deployment",
      desc: "Rigorous testing across browsers and devices followed by zero-downtime production launch and code handover."
    }
  ];

  return (
    <div className="services-page">
      <PageBanner
        badge="WHAT WE DO"
        title="Comprehensive Software Solutions &"
        highlight="Engineering Services"
        subtitle="From responsive React web applications to high-performance Flutter mobile apps and cloud systems, we craft software designed to scale."
      />

      {/* Services Grid */}
      <section className="services-list-section">
        <div className="services-page-container">
          <div className="services-cards-grid">
            {serviceList.map((srv) => (
              <div key={srv.id} className="service-detail-card" id={srv.id}>
                {/* Animated Tech Illustration Scene */}
                <ServiceIllustration type={srv.id} />

                <div className="service-card-top">
                  <span className="service-category">{srv.category}</span>
                  <span className="service-num">{srv.number}</span>
                </div>

                <h3 className="service-title">{srv.title}</h3>
                <p className="service-description">{srv.description}</p>

                <div className="service-features-list">
                  <span className="features-headline">Key Capabilities:</span>
                  {srv.features.map((feat, fIdx) => (
                    <div key={fIdx} className="service-feature-item">
                      <span className="feature-check">⚡</span>
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>

                <div className="service-tech-tags">
                  {srv.tags.map((tag, tIdx) => (
                    <span key={tIdx} className="tech-badge">{tag}</span>
                  ))}
                </div>

                <div className="service-card-footer">
                  <Link to="/contact" className="service-action-link">
                    <span>Inquire About This Service</span>
                    <span className="action-arrow">→</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engineering Process */}
      <section className="services-process-section">
        <div className="services-page-container">
          <div className="text-center section-header">
            <span className="section-eyebrow">OUR METHODOLOGY</span>
            <h2 className="section-title">
              How We Deliver High-Impact <span className="title-gradient">Results</span>
            </h2>
            <p className="section-subtitle">
              A battle-tested 4-step engineering process designed for transparency, speed, and clean execution.
            </p>
          </div>

          <div className="process-grid">
            {processSteps.map((step, idx) => (
              <div key={idx} className="process-card">
                <div className="process-step-num">{step.step}</div>
                <h4 className="process-title">{step.title}</h4>
                <p className="process-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="services-cta-section">
        <div className="services-page-container">
          <div className="services-cta-card">
            <h2>Need a Custom Solution for Your Business?</h2>
            <p>Tell us about your requirements and let our senior developers craft the right solution for you.</p>
            <Link to="/contact" className="btn-primary">
              <span>GET A FREE ESTIMATE & CONSULTATION →</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
